import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { sensitivityFactors } from './scenarioData';

// Impact Bar Component
const ImpactBar = ({ value, maxValue = 100 }) => {
  const isPositive = value >= 0;
  const absValue = Math.abs(value);
  const percentage = Math.min((absValue / maxValue) * 100, 100);
  
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-3 bg-muted/30 rounded-full overflow-hidden relative">
        {isPositive ? (
          <div 
            className="absolute left-1/2 h-full bg-stable rounded-r-full"
            style={{ width: `${percentage / 2}%` }}
          />
        ) : (
          <div 
            className="absolute right-1/2 h-full bg-destructive rounded-l-full"
            style={{ width: `${percentage / 2}%` }}
          />
        )}
        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-border" />
      </div>
      <span className={`text-sm font-bold min-w-16 text-right ${isPositive ? 'text-stable' : 'text-destructive'}`}>
        {isPositive ? '+' : ''}{value}M
      </span>
    </div>
  );
};

// Factor Card Component
const FactorCard = ({ factor, icon: Icon, type }) => {
  const isRisk = type === 'risk';
  
  return (
    <div className={`p-4 rounded-lg border ${isRisk ? 'bg-destructive/5 border-destructive/20' : 'bg-stable/5 border-stable/20'}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`p-2 rounded-lg ${isRisk ? 'bg-destructive/10' : 'bg-stable/10'}`}>
          <Icon className={`w-4 h-4 ${isRisk ? 'text-destructive' : 'text-stable'}`} />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-foreground text-sm">{factor.label}</h4>
          <p className="text-xs text-muted-foreground mt-1">{factor.description}</p>
        </div>
        <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
          {factor.probability}
        </span>
      </div>
      <ImpactBar value={factor.impact} maxValue={70} />
    </div>
  );
};

export const SensitivityAnalysis = () => {
  const factors = Object.values(sensitivityFactors);
  const risks = factors.filter(f => f.impact < 0);
  const opportunities = factors.filter(f => f.impact >= 0);

  return (
    <section className="mb-10" data-testid="sensitivity-analysis-section">
      <div className="flex items-center gap-3 mb-6">
        <Activity className="w-6 h-6 text-warning" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Sensitivity Analysis</h2>
      </div>
      
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            High Impact Variables
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-2">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Risk Factors */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <TrendingDown className="w-4 h-4 text-destructive" />
                <h3 className="text-sm font-semibold text-foreground">Downside Risks</h3>
              </div>
              <div className="space-y-3">
                {risks.map((factor, i) => (
                  <FactorCard key={i} factor={factor} icon={AlertTriangle} type="risk" />
                ))}
              </div>
            </div>
            
            {/* Opportunity Factors */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-stable" />
                <h3 className="text-sm font-semibold text-foreground">Upside Opportunities</h3>
              </div>
              <div className="space-y-3">
                {opportunities.map((factor, i) => (
                  <FactorCard key={i} factor={factor} icon={TrendingUp} type="opportunity" />
                ))}
              </div>
            </div>
          </div>
          
          {/* Legend */}
          <div className="mt-6 pt-4 border-t border-border/50">
            <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-destructive" />
                <span>Negative Impact</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-stable" />
                <span>Positive Impact</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-0.5 h-3 bg-border" />
                <span>Baseline (0)</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default SensitivityAnalysis;
